import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";
import {
  useAtom,
  useAtomValue
} from "jotai";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";
import {toast} from "react-toastify";
import {Link} from "react-router-dom";

import {
  adverCampaignDetailColumn,
  adverStatusAtom,
  adverStatusDetailAtom,
  userCampaignListColumn
} from "./entity/Campaign";
import {
  searchConditionAtom
} from "./entity/Common";
import {
  tokenResultAtom
} from "../login/entity/Common";
import {
  retrieveAdvertiserCampaignStatus
} from "../../services/dash_board/ManageCampaignAxios";
import {
  selCampaignBudgetScheduleHistory
} from "../../services/campaign/BudgetAxios";
import Table from "../../components/table";
import SwitchComponent from "../../components/switch";
import {
  updateAdverCampaignPublish,
  updateCampaignPublish
} from "../../services/campaign/CreativeAxios";

const GENDER_LABELS = {MALE: "남성", FEMALE: "여성"};
const AGE_LABELS = {
  AGE_10_19: "10대",
  AGE_20_29: "20대",
  AGE_30_39: "30대",
  AGE_40_49: "40대",
  AGE_50_59: "50대",
  AGE_60_PLUS: "60대 이상",
  UNKNOWN: "연령 미확인"
};

const CAMPAIGN_STEP_META = {
  INIT: {label: "기본 정보", tone: "basic"},
  STEP2_BUDGET: {label: "예산·입찰", tone: "budget"},
  STEP3_INVENTORY: {label: "광고그룹", tone: "group"},
  STEP4_CREATIVE: {label: "소재 설정", tone: "creative"},
  COMPLETED: {label: "설정 완료", tone: "completed"}
};

const getCampaignStepMeta = step =>
    CAMPAIGN_STEP_META[step] ?? {
      label: step || "미설정",
      tone: "default"
    };

const formatTargetValues = (values, labels) =>
    Array.isArray(values) && values.length > 0
        ? values.map(value => labels[value] ?? value).join(", ")
        : "전체";

const extractSchedules = response => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.schedules)) return response.schedules;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.dataEntity?.data)) return response.dataEntity.data;
  return [];
};

function CampaignBoard({
  showHeader = false
}) {
  const tokenUserInfo =
      useAtomValue(tokenResultAtom);

  const [advertiserData] =
      useAtom(adverStatusAtom);

  const [
    campaignDetailData,
    setCampaignDetailData
  ] = useAtom(adverStatusDetailAtom);

  const searchCondition =
      useAtomValue(searchConditionAtom);

  const [
    selectedAdvertiser,
    setSelectedAdvertiser
  ] = useState(null);

  const [
    isLoadingCampaigns,
    setIsLoadingCampaigns
  ] = useState(false);

  const [selectedCampaign, setSelectedCampaign] =
      useState(null);

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const detailLoadingTimerRef = useRef(null);

  useEffect(() => () => {
    if (detailLoadingTimerRef.current) {
      clearTimeout(detailLoadingTimerRef.current);
    }
  }, []);

  const handleCampaignSelect = campaign => {
    if (detailLoadingTimerRef.current) {
      clearTimeout(detailLoadingTimerRef.current);
    }

    setSelectedCampaign(campaign);
    setIsDetailOpen(true);
    setIsDetailLoading(true);

    detailLoadingTimerRef.current = setTimeout(() => {
      setIsDetailLoading(false);
      detailLoadingTimerRef.current = null;
    }, 450);
  };

  const handleDetailPublishChange = async () => {
    if (!selectedCampaign?.campaignId) {
      return false;
    }

    const nextPublish = selectedCampaign.publishYn !== "Y";
    const result = await (
        tokenUserInfo?.role === "NORMAL"
            ? updateAdverCampaignPublish(selectedCampaign.campaignId, nextPublish)
            : updateCampaignPublish(selectedCampaign.campaignId, nextPublish)
    );

    if (!result) {
      toast.error("게재 상태를 변경하지 못했습니다.", {
        toastId: "campaignPanelPublishError"
      });
      return false;
    }

    const publishYn = nextPublish ? "Y" : "N";
    setSelectedCampaign(prev => ({
      ...prev,
      publishYn
    }));
    setCampaignDetailData(prev =>
        (Array.isArray(prev) ? prev : []).map(campaign =>
            campaign?.campaignId === selectedCampaign.campaignId
                ? {...campaign, publishYn}
                : campaign
        )
    );
    return true;
  };

  const advertiserList = useMemo(
      () =>
          Array.isArray(advertiserData)
              ? advertiserData.slice().sort((first, second) =>
                  String(first?.adverName ?? first?.username ?? "")
                  .localeCompare(
                      String(second?.adverName ?? second?.username ?? ""),
                      "ko"
                  )
              )
              : [],
      [advertiserData]
  );

  const campaignList = useMemo(
      () =>
          Array.isArray(campaignDetailData)
              ? campaignDetailData
              : [],
      [campaignDetailData]
  );

  const compactCampaignColumns = useMemo(() => {
    const publishColumn = adverCampaignDetailColumn.find(column => column.name === "publishYn");

    return [
      publishColumn,
      {
        name: "campaignName",
        header: "캠페인명",
        minWidth: 190,
        defaultFlex: 1.7,
        showColumnMenuTool: false,
        render: ({value}) => <CampaignNameText>{value ?? "-"}</CampaignNameText>
      },
      {
        name: "step",
        header: "설정 단계",
        minWidth: 105,
        defaultFlex: 0.85,
        textAlign: "center",
        showColumnMenuTool: false,
        render: ({value}) => {
          const stepMeta = getCampaignStepMeta(value);
          return (
              <CampaignStepStatus $tone={stepMeta.tone} title={value ?? ""}>
                {stepMeta.label}
              </CampaignStepStatus>
          );
        }
      },
      {
        name: "productType",
        header: "상품",
        minWidth: 90,
        defaultFlex: 0.8,
        textAlign: "center",
        showColumnMenuTool: false,
        render: ({value}) => value ?? "-"
      },
      {
        name: "creativeName",
        header: "소재",
        minWidth: 82,
        defaultFlex: 0.7,
        textAlign: "center",
        showColumnMenuTool: false,
        render: ({value}) => <CreativeStatus $ready={Boolean(value)}>{value ? "등록" : "미등록"}</CreativeStatus>
      },
      {
        name: "budgetReservationYn",
        header: "예산 예약",
        minWidth: 86,
        maxWidth: 96,
        textAlign: "center",
        sortable: false,
        showColumnMenuTool: false,
        render: ({value}) => (
            <ReservationStatus $active={value === "Y"}>
              {value === "Y" ? "ON" : "OFF"}
            </ReservationStatus>
        )
      }
    ].filter(Boolean);
  }, []);

  const isAdmin =
      tokenUserInfo?.role !== "NORMAL";

  const selectedDemographic =
      selectedCampaign?.demographicTarget ??
      selectedCampaign?.inventoryDetail?.demographicTarget;
  const selectedGenderText = formatTargetValues(
      selectedDemographic?.genders,
      GENDER_LABELS
  );
  const selectedAgeText = formatTargetValues(
      selectedDemographic?.ageGroups,
      AGE_LABELS
  );

  /*
   * 광고주 검색 결과가 바뀌었는데 기존 선택 광고주가
   * 목록에 없으면 상세 영역을 초기화한다.
   */
  useEffect(() => {
    if (!selectedAdvertiser) {
      return;
    }

    const selectedExists =
        advertiserList.some(
            advertiser =>
                advertiser.userId ===
                selectedAdvertiser.userId
        );

    if (!selectedExists) {
      setSelectedAdvertiser(null);
      setCampaignDetailData([]);
    }
  }, [
    advertiserList,
    selectedAdvertiser,
    setCampaignDetailData
  ]);

  const handleSelectAdvertiser =
      async advertiser => {
        const userId =
            advertiser?.userId;

        if (
            !userId ||
            isLoadingCampaigns
        ) {
          return;
        }

        if (selectedAdvertiser?.userId === userId) {
          setSelectedAdvertiser(null);
          setSelectedCampaign(null);
          setCampaignDetailData([]);
          return;
        }

        setSelectedAdvertiser(
            advertiser
        );
        setSelectedCampaign(null);

        setIsLoadingCampaigns(true);
        setCampaignDetailData([]);

        try {
          const response =
              await retrieveAdvertiserCampaignStatus(
                  userId,
                  searchCondition
              );

          const campaigns =
              Array.isArray(response)
                  ? response
                  : [];

          const normalizedCampaigns =
              (await Promise.all(campaigns.map(async item => {
                const scheduleResponse =
                    await selCampaignBudgetScheduleHistory(item.campaignId);
                const hasActiveBudgetReservation =
                    extractSchedules(scheduleResponse).some(schedule =>
                        ["WAITING", "PROCESSING"].includes(schedule?.status)
                    );

                return {
                  ...item,
                  userId,
                  budgetReservationYn: hasActiveBudgetReservation ? "Y" : "N",
                  adverInfo:
                      `${advertiser?.adverName ?? "-"} (${advertiser?.username ?? "-"})`
                };
              }))).sort((first, second) =>
                  String(first?.campaignName ?? "").localeCompare(
                      String(second?.campaignName ?? ""),
                      "ko"
                  )
              );

          setCampaignDetailData(normalizedCampaigns);
          setSelectedCampaign(normalizedCampaigns[0] ?? null);
        } catch (error) {
          console.error(
              "광고주 캠페인 목록 조회 실패:",
              error
          );

          setCampaignDetailData([]);

          toast.error(
              "선택한 광고주의 캠페인 정보를 불러오는 중 오류가 발생했습니다.",
              {
                toastId:
                    "advertiserCampaignListLoadError"
              }
          );
        } finally {
          setIsLoadingCampaigns(false);
        }
      };

  if (!isAdmin) {
    return (
        <UserCampaignSection>
          {showHeader && (
              <SectionHeader>
                <div>
                  <SectionTitle>
                    캠페인 현황
                  </SectionTitle>

                  <SectionDescription>
                    내 캠페인과 소재 정보를 확인합니다.
                  </SectionDescription>
                </div>

                <CountBadge>
                  {advertiserList.length}건
                </CountBadge>
              </SectionHeader>
          )}

          <UserTableCard>
            <Table
                columns={
                  userCampaignListColumn
                }
                totalCount={[
                  advertiserList.length,
                  "캠페인"
                ]}
                rowHeight={null}
                defaultSortInfo={{
                  name: "username",
                  dir: -1
                }}
                data={advertiserList}
            />
          </UserTableCard>
        </UserCampaignSection>
    );
  }

  return (
      <MasterDetailLayout>
        {showHeader && (
            <SectionHeader>
              <div>
                <SectionTitle>
                  광고주별 캠페인 현황
                </SectionTitle>

                <SectionDescription>
                  광고주를 선택하면 하단에 해당 광고주의
                  캠페인 목록이 표시됩니다.
                </SectionDescription>
              </div>

              <CountBadge>
                {advertiserList.length}명
              </CountBadge>
            </SectionHeader>
        )}

        <TableSection>
          <TableSectionHeader>
            <TableSectionTitleGroup>
              <div>
                <TableSectionTitle>
                  광고주 목록
                </TableSectionTitle>

                <TableSectionDescription>
                  캠페인을 확인할 광고주를 선택해주세요.
                </TableSectionDescription>
              </div>
            </TableSectionTitleGroup>

            <CountBadge>
              {advertiserList.length}명
            </CountBadge>
          </TableSectionHeader>

          <AdvertiserTableCard>
            <AdvertiserTableHeader>
              <span>광고주</span>
              <span>캠페인</span>
            </AdvertiserTableHeader>

            <AdvertiserTableBody>
              {advertiserList.length ===
              0 ? (
                  <AdvertiserEmpty>
                    조회된 광고주가 없습니다.
                  </AdvertiserEmpty>
              ) : (
                  advertiserList.map(
                      advertiser => {
                        const selected =
                            selectedAdvertiser
                                ?.userId ===
                            advertiser.userId;

                        return (
                            <AdvertiserRow
                                key={
                                  advertiser.userId
                                }
                                type="button"
                                $selected={selected}
                                aria-pressed={selected}
                                onClick={() =>
                                    handleSelectAdvertiser(
                                        advertiser
                                    )
                                }
                            >
                              <AdvertiserNameCell>
                                <AdvertiserTextGroup>
                                  <AdvertiserName>
                                    {advertiser?.adverName ?? "-"}
                                  </AdvertiserName>
                                  <AdvertiserId>
                                    {advertiser?.username ?? "-"}
                                  </AdvertiserId>
                                </AdvertiserTextGroup>

                                {selected && (
                                    <SelectedBadge>
                                      선택됨
                                    </SelectedBadge>
                                )}
                              </AdvertiserNameCell>

                              <CampaignCountCell>
                                <CampaignCountBadge>
                                  {Number(
                                      advertiser
                                          ?.creativeCount ??
                                      0
                                  )}
                                </CampaignCountBadge>
                              </CampaignCountCell>

                            </AdvertiserRow>
                        );
                      }
                  )
              )}
            </AdvertiserTableBody>
          </AdvertiserTableCard>
        </TableSection>

        <TableSection>
          <TableSectionHeader>
            <TableSectionTitleGroup>

              <div>
                <TableSectionTitle>
                  캠페인 목록
                </TableSectionTitle>

                <TableSectionDescription>
                  {selectedAdvertiser
                      ? `${selectedAdvertiser.adverName ?? "-"} 광고주의 캠페인입니다.`
                      : "상단 광고주 목록에서 광고주를 선택해주세요."}
                </TableSectionDescription>
              </div>

              {selectedAdvertiser && (
                  <CampaignListCount>
                    {campaignList.length}개 캠페인
                  </CampaignListCount>
              )}
            </TableSectionTitleGroup>

            {selectedAdvertiser && (
                <SelectedAdvertiserSummary>
                  <SummaryLabel>
                    선택 광고주
                  </SummaryLabel>

                  <SummaryValue>
                    {selectedAdvertiser
                        .adverName ?? "-"}
                  </SummaryValue>

                  <SummaryDivider />

                  <SummarySubValue>
                    {selectedAdvertiser
                        .username ?? "-"}
                  </SummarySubValue>
                </SelectedAdvertiserSummary>
            )}
          </TableSectionHeader>

          <CampaignTableCard
              $active={Boolean(selectedAdvertiser)}
          >
            {!selectedAdvertiser ? (
                <CampaignEmptyState>
                  <EmptyIcon
                      aria-hidden="true"
                  >
                    <svg
                        width="30"
                        height="30"
                        viewBox="0 0 24 24"
                    >
                      <path
                          fill="currentColor"
                          d="M4 5h16v14H4V5Zm2 2v10h12V7H6Zm2 2h8v2H8V9Zm0 4h5v2H8v-2Z"
                      />
                    </svg>
                  </EmptyIcon>

                  <EmptyTitle>
                    광고주를 선택해주세요.
                  </EmptyTitle>

                  <EmptyDescription>
                    광고주를 클릭하면 해당 광고주의 캠페인
                    목록이 이 영역에 표시됩니다.
                  </EmptyDescription>
                </CampaignEmptyState>
            ) : isLoadingCampaigns ? (
                <CampaignLoading>
                  <LoadingSpinner />

                  <span>
                캠페인 정보를 불러오고 있습니다.
              </span>
                </CampaignLoading>
            ) : (
                <CampaignGridWrap>
                  <StyledCampaignGrid
                      style={{
                        height: 492
                      }}
                      licenseKey={
                        process.env
                            .REACT_APP_DATA_GRID_LICENSE_KEY
                      }
                      handle={null}
                      activeCell={null}
                      dataSource={
                        campaignList
                      }
                      columns={
                        compactCampaignColumns
                      }
                      defaultSortInfo={{
                        name: "campaignName",
                        dir: 1
                      }}
                      enableColumnAutosize={
                        true
                      }
                      emptyText="등록된 캠페인이 없습니다."
                      rowHeight={58}
                      headerHeight={48}
                      showHoverRows={true}
                      groups={false}
                      idProperty="campaignId"
                      rowClassName={({data}) =>
                          String(data?.campaignId) ===
                          String(selectedCampaign?.campaignId)
                              ? "campaign-board-selected-row"
                              : undefined
                      }
                      onRowClick={({data}) => {
                        handleCampaignSelect(data);
                      }}
                  />
                </CampaignGridWrap>
            )}
          </CampaignTableCard>
        </TableSection>

        <DetailPanelOverlay
            $open={isDetailOpen}
            onClick={() => setIsDetailOpen(false)}
            aria-hidden="true"
        />

        <CampaignDetailPanel $open={isDetailOpen}>
          {isDetailLoading ? (
              <DetailLoading role="status" aria-live="polite">
                <LoadingSpinner />
                <strong>캠페인 정보를 불러오고 있습니다.</strong>
                <span>선택한 캠페인의 미리보기를 준비합니다.</span>
              </DetailLoading>
          ) : !selectedCampaign ? (
              <DetailEmpty>
                <DetailEmptyIcon>i</DetailEmptyIcon>
                <strong>캠페인을 선택해 주세요</strong>
                <span>선택한 캠페인의 설정 정보와 수정 메뉴가 표시됩니다.</span>
              </DetailEmpty>
          ) : (
              <>
                <DetailHeader>
                  <DetailCloseButton
                      type="button"
                      aria-label="캠페인 정보 닫기"
                      onClick={() => setIsDetailOpen(false)}
                  >×</DetailCloseButton>
                  <DetailEyebrow>선택 캠페인</DetailEyebrow>
                  <DetailCampaignName>
                    {selectedCampaign.campaignName ?? "-"}
                  </DetailCampaignName>
                  <PublishBadge $active={selectedCampaign.publishYn === "Y"}>
                    {selectedCampaign.publishYn === "Y" ? "게재 중" : "중지"}
                  </PublishBadge>
                </DetailHeader>

                <DetailSection
                    to={{pathname: "/board/campaignLookOver", search: `campaignId=${selectedCampaign.campaignId}&userId=${selectedCampaign.userId}&adverInfo=${selectedCampaign.adverInfo}`}}
                    state={{campaignId: selectedCampaign.campaignId, userId: selectedCampaign.userId, adverInfo: selectedCampaign.adverInfo}}
                >
                  <DetailSectionHeader>
                    <strong>기본 정보</strong>
                    <DetailArrow>수정 ›</DetailArrow>
                  </DetailSectionHeader>
                  <DetailRow><span>상품</span><b>{selectedCampaign.productType ?? "-"}</b></DetailRow>
                </DetailSection>

                <DetailSection to={{pathname: "/board/campaignTwo", search: `campaignId=${selectedCampaign.campaignId}&userId=${selectedCampaign.userId}&adverInfo=${selectedCampaign.adverInfo}`}}>
                  <DetailSectionHeader>
                    <strong>예산 · 입찰</strong>
                    <DetailArrow>수정 ›</DetailArrow>
                  </DetailSectionHeader>
                  <DetailRow><span>일 예산</span><b>{selectedCampaign.infiniteBudgetYn !== "N" ? "무제한" : `${Number(selectedCampaign.dailyAvgBudget ?? 0).toLocaleString("ko-KR")}원`}</b></DetailRow>
                  <DetailRow><span>예산 그룹</span><b>{selectedCampaign.groupName ?? "-"}</b></DetailRow>
                </DetailSection>

                <DetailSection
                    to={{pathname: "/board/campaignThree", search: `campaignId=${selectedCampaign.campaignId}&adverInfo=${selectedCampaign.adverInfo}`}}
                    state={{campaignId: selectedCampaign.campaignId, adverInfo: selectedCampaign.adverInfo}}
                >
                  <DetailSectionHeader>
                    <strong>게재 · 타겟팅</strong>
                    <DetailArrow>수정 ›</DetailArrow>
                  </DetailSectionHeader>
                  <DetailRow><span>연결 지면</span><b>{selectedCampaign.allowInventorys?.length ?? 0}개</b></DetailRow>
                  <DetailRow><span>성별</span><b>{selectedGenderText}</b></DetailRow>
                  <DetailRow><span>연령</span><b>{selectedAgeText}</b></DetailRow>
                </DetailSection>

                <DetailSection
                    to={{pathname: "/board/campaignFour", search: `campaignId=${selectedCampaign.campaignId}&creativeType=${selectedCampaign.creativeType}&productType=${selectedCampaign.productType}&adverInfo=${selectedCampaign.adverInfo}`}}
                    state={{campaignId: selectedCampaign.campaignId, creativeType: selectedCampaign.creativeType, productType: selectedCampaign.productType, adverInfo: selectedCampaign.adverInfo}}
                >
                  <DetailSectionHeader>
                    <strong>소재</strong>
                    <DetailArrow>수정 ›</DetailArrow>
                  </DetailSectionHeader>
                  <DetailRow><span>소재 유형</span><b>{selectedCampaign.creativeType ?? selectedCampaign.productType ?? "-"}</b></DetailRow>
                  <DetailRow><span>소재 상태</span><b>{selectedCampaign.creativeName ? "등록 완료" : "미등록"}</b></DetailRow>
                </DetailSection>

                <FullDetailLink
                    to={{pathname: "/board/campaignLookOver", search: `campaignId=${selectedCampaign.campaignId}&userId=${selectedCampaign.userId}&adverInfo=${selectedCampaign.adverInfo}`}}
                    state={{campaignId: selectedCampaign.campaignId, userId: selectedCampaign.userId, adverInfo: selectedCampaign.adverInfo}}
                >전체 정보 보기</FullDetailLink>
              </>
          )}
        </CampaignDetailPanel>
      </MasterDetailLayout>
  );
}

export default CampaignBoard;

const MasterDetailLayout =
    styled.section`
    display: grid;
    width: 100%;
    grid-template-columns: 250px minmax(560px, 1fr) 290px;
    align-items: start;
    gap: 16px;

    @media (max-width: 1320px) {
      grid-template-columns: 230px minmax(520px, 1fr);
    }

    @media (max-width: 900px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `;

const UserCampaignSection =
    styled.section`
    width: 100%;
  `;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const DetailPublishControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 8px;
  background: rgba(255, 255, 255, .08);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
`;

const SectionTitle = styled.div`
  margin-bottom: 5px;
  color: #222;
  font-size: 15px;
  font-weight: 700;
`;

const SectionDescription =
    styled.div`
    color: #888;
    font-size: 11px;
    line-height: 1.5;
  `;

const CountBadge = styled.span`
  display: inline-flex;
  min-width: 44px;
  height: 26px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 10px;
  font-weight: 700;
`;

const TableSection = styled.section`
  width: 100%;
`;

const TableSectionHeader = styled.div`
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 10px;

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 9px;
  }
`;

const TableSectionTitleGroup =
    styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
  `;

const CampaignListCount = styled.span`
  display: inline-flex;
  min-height: 25px;
  align-items: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 999px;
  background: #fff6ed;
  color: #d96500;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
`;

const TableSectionNumber =
    styled.span`
    display: inline-flex;
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    border: 1px solid
      ${({$inactive}) =>
        $inactive
            ? "#d8d8d8"
            : "#f5811f"};
    border-radius: 50%;
    background-color:
      ${({$inactive}) =>
        $inactive
            ? "#f5f5f5"
            : "#fff4e8"};
    color:
      ${({$inactive}) =>
        $inactive
            ? "#999"
            : "#f5811f"};
    font-size: 11px;
    font-weight: 700;
  `;

const TableSectionTitle =
    styled.div`
    margin-bottom: 4px;
    color: #333;
    font-size: 13px;
    font-weight: 700;
  `;

const TableSectionDescription =
    styled.div`
    color: #888;
    font-size: 10px;
    line-height: 1.45;
  `;

const AdvertiserTableCard =
    styled.div`
    display: flex;
    width: 100%;
    height: 492px;
    overflow: hidden;
    flex-direction: column;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    background-color: #fff;
  `;

const AdvertiserTableHeader =
    styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 62px;
    min-height: 44px;
    align-items: center;
    padding: 0 14px;
    border-bottom: 1px solid #e5e5e5;
    background-color: #f7f7f7;
    color: #666;
    font-size: 11px;
    font-weight: 600;
  `;

const AdvertiserTableBody =
    styled.div`
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color:
      #c7c7c7 transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background-color: #c7c7c7;
    }
  `;

const AdvertiserRow = styled.button`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) 62px;
  min-height: 56px;
  align-items: center;
  padding: 0 14px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background-color:
    ${({$selected}) =>
    $selected
        ? "#fff8f1"
        : "#fff"};
  color: #555;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: #fffaf6;
  }

  &:focus-visible {
    position: relative;
    z-index: 1;
    outline: 1px solid #f5811f;
    outline-offset: -1px;
  }
`;

const SelectedMarkCell =
    styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

const SelectedMark = styled.span`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1px solid
    ${({$selected}) =>
    $selected
        ? "#f5811f"
        : "#ccc"};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color:
      ${({$selected}) =>
    $selected
        ? "#f5811f"
        : "transparent"};
    content: "";
    transform:
      translate(-50%, -50%);
  }
`;

const AdvertiserNameCell =
    styled.span`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
  `;

const AdvertiserTextGroup = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
`;

const AdvertiserName = styled.span`
  overflow: hidden;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AdvertiserId = styled.span`
  overflow: hidden;
  color: #999;
  font-size: 9px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedBadge = styled.span`
  display: inline-flex;
  min-width: 42px;
  height: 21px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 9px;
  font-weight: 700;
`;

const TableCell = styled.span`
  overflow: hidden;
  padding: 0 8px;
  color: #666;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CampaignCountCell =
    styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

const CampaignCountBadge =
    styled.span`
    display: inline-flex;
    min-width: 38px;
    height: 24px;
    align-items: center;
    justify-content: center;
    padding: 0 9px;
    border-radius: 12px;
    background-color: #f5f5f5;
    color: #555;
    font-size: 10px;
    font-weight: 700;
  `;

const RowArrow = styled.span`
  display: inline-flex;
  width: 27px;
  height: 27px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color:
    ${({$selected}) =>
    $selected
        ? "#fff4e8"
        : "#f5f5f5"};
  color:
    ${({$selected}) =>
    $selected
        ? "#f5811f"
        : "#999"};
`;

const AdvertiserEmpty = styled.div`
  padding: 42px 20px;
  color: #999;
  font-size: 11px;
  text-align: center;
`;

const CampaignTableCard =
    styled.div`
    height: 492px;
    overflow: hidden;
    border: ${({$active}) => $active ? "0" : "1px solid #e5e5e5"};
    border-radius: 12px;
    background-color: #fff;
    box-shadow: ${({$active}) =>
      $active
          ? "0 8px 24px rgba(61, 45, 33, 0.07)"
          : "none"};
  `;

const SelectedAdvertiserSummary =
    styled.div`
    display: flex;
    min-height: 34px;
    align-items: center;
    gap: 7px;
    padding: 0 12px;
    border: 1px solid #f1d4c2;
    border-radius: 17px;
    background-color: #fffaf6;
  `;

const SummaryLabel = styled.span`
  color: #888;
  font-size: 9px;
`;

const SummaryValue = styled.strong`
  color: #f5811f;
  font-size: 10px;
`;

const SummaryDivider = styled.span`
  width: 1px;
  height: 11px;
  background-color: #e3c9b7;
`;

const SummarySubValue = styled.span`
  color: #777;
  font-size: 9px;
`;

const CampaignGridWrap =
    styled.div`
    width: 100%;
    height: 492px;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #e7d8cc;
    border-radius: 12px;
  `;

const StyledCampaignGrid =
    styled(ReactDataGrid)`
    width: 100% !important;
    min-width: 0;
    min-height: 164px !important;
    border: 0 !important;
    color: #3f4750;
    font-size: 12px;

    .InovuaReactDataGrid__header,
    .InovuaReactDataGrid__column-header {
      background: linear-gradient(180deg, #fffaf6 0%, #fff4e9 100%);
    }

    .InovuaReactDataGrid__column-header {
      border-right-color: #ecdcd0;
      border-bottom: 2px solid #f0c5a3;
      color: #4b5158;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: -0.01em;
    }

    .InovuaReactDataGrid__row {
      border-bottom-color: #edf0f2;
      background: #fff;
      color: #4c545c;
      transition: background-color 140ms ease, box-shadow 140ms ease;
    }

    .InovuaReactDataGrid__row-even {
      background: #fcfcfd;
    }

    .InovuaReactDataGrid__row:hover {
      z-index: 1;
      background-color: #fff8f1;
      box-shadow: inset 3px 0 0 #f5811f;
    }

    .campaign-board-selected-row {
      z-index: 1;
      background: linear-gradient(90deg, #fff0e2 0%, #fff8f1 100%) !important;
      color: #343a40;
      box-shadow:
        inset 4px 0 0 #f5811f,
        inset 0 1px 0 rgba(245, 129, 31, 0.18),
        inset 0 -1px 0 rgba(245, 129, 31, 0.18);
    }

    .campaign-board-selected-row:hover {
      background: linear-gradient(90deg, #ffe9d4 0%, #fff4e9 100%) !important;
      box-shadow:
        inset 4px 0 0 #e96f0a,
        inset 0 1px 0 rgba(245, 129, 31, 0.24),
        inset 0 -1px 0 rgba(245, 129, 31, 0.24);
    }

    .campaign-board-selected-row .InovuaReactDataGrid__cell {
      font-weight: 600;
    }

    .InovuaReactDataGrid__cell {
      padding-right: 14px;
      padding-left: 14px;
      border-right-color: #edf0f2;
    }

    .InovuaReactDataGrid__cell a {
      color: #b95b0b;
      font-weight: 700;
      text-decoration: none;
    }

    .InovuaReactDataGrid__cell a:hover {
      color: #e36f0d;
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    .InovuaReactDataGrid__cell--active,
    .InovuaReactDataGrid__cell--selected {
      background: rgba(245, 129, 31, 0.06) !important;
      box-shadow: inset 0 0 0 1px rgba(245, 129, 31, 0.3);
    }
  `;

const CampaignNameText = styled.span`
  display: block;
  overflow: hidden;
  color: #3e454b;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CampaignStepStatus = styled.span`
  display: inline-flex;
  min-width: 68px;
  min-height: 22px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid ${({$tone}) => ({
    basic: "#d9dde1",
    budget: "#f1d1b7",
    group: "#cbd9f1",
    creative: "#d9d3f3",
    completed: "#c7e6d6",
    default: "#dfe2e5"
  }[$tone])};
  border-radius: 999px;
  background: ${({$tone}) => ({
    basic: "#f4f5f6",
    budget: "#fff5ec",
    group: "#f1f6ff",
    creative: "#f5f3ff",
    completed: "#edf9f3",
    default: "#f4f5f6"
  }[$tone])};
  color: ${({$tone}) => ({
    basic: "#687078",
    budget: "#d76c15",
    group: "#4f75b7",
    creative: "#7062c8",
    completed: "#258652",
    default: "#7c8389"
  }[$tone])};
  font-size: 9px;
  font-weight: 800;
  white-space: nowrap;
`;

const CreativeStatus = styled.span`
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 999px;
  background: ${({$ready}) => $ready ? "#eaf8f0" : "#f2f3f4"};
  color: ${({$ready}) => $ready ? "#258652" : "#7c8389"};
  font-size: 9px;
  font-weight: 800;
`;

const ReservationStatus = styled.span`
  display: inline-flex;
  min-width: 38px;
  min-height: 22px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({$active}) => $active ? "#f1c39d" : "#dfe2e5"};
  border-radius: 999px;
  background: ${({$active}) => $active ? "#fff4e8" : "#f3f4f5"};
  color: ${({$active}) => $active ? "#d96500" : "#858c92"};
  font-size: 9px;
  font-weight: 800;
`;

const CampaignEmptyState =
    styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    min-height: 492px;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 32px 20px;
    text-align: center;
  `;

const EmptyIcon = styled.span`
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  margin-bottom: 13px;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: #aaa;
`;

const EmptyTitle = styled.div`
  margin-bottom: 7px;
  color: #555;
  font-size: 13px;
  font-weight: 700;
`;

const EmptyDescription =
    styled.div`
    color: #999;
    font-size: 10px;
    line-height: 1.5;
  `;

const CampaignLoading = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 492px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #777;
  font-size: 11px;
`;

const CampaignDetailPanel = styled.aside`
  position: sticky;
  top: 18px;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  padding: 18px;
  border: 1px solid #e7d8cc;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(61, 45, 33, 0.07);

  @media (max-width: 1320px) {
    position: static;
    grid-column: 1 / -1;
    height: auto;
    min-height: auto;
  }

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 232;
    width: min(360px, 90vw);
    height: 100vh;
    max-height: 100vh;
    padding-top: 52px;
    border-radius: 14px 0 0 14px;
    transform: translateX(${({$open}) => $open ? "0" : "105%"});
    transition: transform .22s ease;
    box-shadow: -18px 0 42px rgba(15, 23, 42, .2);
  }
`;

const DetailPanelOverlay = styled.button`
  display: none;

  @media (max-width: 900px) {
    position: fixed;
    inset: 0;
    z-index: 231;
    display: ${({$open}) => $open ? "block" : "none"};
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: rgba(15, 23, 42, .42);
  }
`;

const DetailEmpty = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #999;
  font-size: 10px;
  line-height: 1.6;
  text-align: center;
`;

const DetailLoading = styled.div`
  display: flex;
  height: 100%;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #777;
  text-align: center;

  & > span:first-child {
    width: 24px;
    height: 24px;
    margin-bottom: 14px;
  }

  strong {
    margin-bottom: 5px;
    color: #4b5158;
    font-size: 11px;
  }

  span {
    color: #999;
    font-size: 9px;
  }
`;

const DetailEmptyIcon = styled.span`
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border-radius: 50%;
  background: #fff4e8;
  color: #f5811f;
`;

const DetailHeader = styled.header`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
`;

const DetailCloseButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    position: fixed;
    top: 12px;
    right: 14px;
    z-index: 1;
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #e5e8eb;
    border-radius: 8px;
    background: #fff;
    color: #59616a;
    font-size: 20px;
    cursor: pointer;
  }
`;

const DetailEyebrow = styled.div`
  flex: 0 0 auto;
  margin-bottom: 5px;
  color: #f5811f;
  font-size: 9px;
  font-weight: 800;
`;

const DetailCampaignName = styled.h3`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  margin: 0;
  color: #2f3439;
  font-size: 15px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PublishBadge = styled.span`
  display: inline-flex;
  min-width: 48px;
  min-height: 23px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 999px;
  background: ${({$active}) => $active ? "#eaf8f0" : "#f2f3f4"};
  color: ${({$active}) => $active ? "#258652" : "#7c8389"};
  font-size: 9px;
  font-weight: 800;
  white-space: nowrap;
`;

const DetailSection = styled(Link)`
  display: block;
  margin: 0 -8px;
  padding: 15px 8px;
  border-bottom: 1px solid #f0f0f0;
  border-radius: 7px;
  color: inherit;
  text-decoration: none;
  transition: background 140ms ease, transform 140ms ease;

  &:hover {
    background: #fff8f1;
    transform: translateX(2px);
  }
`;

const DetailSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #444;
  font-size: 11px;
  font-weight: 800;
`;

const DetailArrow = styled.span`
  color: #d96500;
  font-size: 9px;
  font-weight: 700;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 8px;
  margin-top: 8px;
  font-size: 10px;
  line-height: 1.45;

  span { color: #999; }
  b {
    overflow: hidden;
    color: #555;
    font-weight: 600;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FullDetailLink = styled(Link)`
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  border-radius: 7px;
  background: #f5811f;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;

  &:hover { background: #df6e10; }
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 2px solid #f3d5c0;
  border-top-color: #f5811f;
  border-radius: 50%;
  animation:
    campaign-board-spin
    0.7s linear infinite;

  @keyframes campaign-board-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const UserTableCard = styled.div`
  width: 100%;
  min-height: 240px;
  overflow-x: auto;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;

  & > div {
    border: 0;
  }
`;
